import { useState } from "react";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TIP_MIN = 1;
const TIP_MAX = 500;

export function validateTip(raw) {
  const s = String(raw).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return "Enter an amount like 7 or 7.50 (max 2 decimals).";
  const v = Number(s);
  if (v < TIP_MIN) return `The minimum tip is $${TIP_MIN.toFixed(2)}.`;
  if (v > TIP_MAX) return `The maximum tip is $${TIP_MAX.toFixed(2)}.`;
  return null;
}

// Branded custom-amount tip flow: enter → review → confirm. The server recalculates and splits 60/40.
export function TipDialog({ open, onOpenChange, creator, onCheckout }) {
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("enter");
  const [busy, setBusy] = useState(false);
  const error = amount ? validateTip(amount) : null;
  const close = (v) => { onOpenChange(v); if (!v) { setStep("enter"); setAmount(""); } };
  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pay/tips", { creator_id: creator.id, amount: Number(amount) });
      close(false);
      onCheckout(data.hosted_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="tip-dialog" className="max-w-sm border-[#3b1a5c] bg-[#0a070e] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gift size={18} className="text-[#d42be8]" /> Tip {creator?.display_name}</DialogTitle>
          <DialogDescription className="text-xs text-[#a7a0af]">PEEPING PAY sandbox — no real card is charged and no card details are collected here.</DialogDescription>
        </DialogHeader>
        {step === "enter" ? (
          <div className="flex flex-col gap-3">
            <label className="text-xs text-[#a7a0af]">Your amount (USD)</label>
            <div className="flex items-center rounded-2xl border border-white/15 bg-[#111015] px-4 focus-within:border-[#a03cff]">
              <span className="text-2xl text-[#c77dff]">$</span>
              <input autoFocus inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                placeholder="0.00" data-testid="tip-amount-input" className="w-full bg-transparent px-2 py-4 text-3xl font-bold outline-none" />
            </div>
            <p data-testid="tip-amount-error" className="min-h-[1rem] text-xs text-red-300">{error}</p>
            <p className="text-[0.65rem] text-[#6f6878]">Min ${TIP_MIN.toFixed(2)} · max ${TIP_MAX.toFixed(2)}. Any amount you choose.</p>
            <button disabled={!amount || !!error} onClick={() => setStep("review")} data-testid="tip-review-btn" className="btn-brand rounded-full py-3 text-sm font-semibold disabled:opacity-40">Review tip</button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div data-testid="tip-review" className="rounded-2xl border border-white/10 p-4 text-sm">
              <div className="flex justify-between"><span className="text-[#a7a0af]">Tip for</span><span>{creator?.display_name}</span></div>
              <div className="mt-2 flex justify-between text-lg font-bold"><span>Total</span><span data-testid="tip-review-amount">${Number(amount).toFixed(2)}</span></div>
              <p className="mt-2 text-[0.65rem] text-[#6f6878]">One-time payment. The creator receives 60% after processing fees; PEEPING retains 40%. Calculated on our servers.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep("enter")} data-testid="tip-edit-btn" className="flex-1 rounded-full border border-white/15 py-3 text-sm">Edit</button>
              <button disabled={busy} onClick={confirm} data-testid="tip-confirm-btn" className="btn-brand flex-[1.5] rounded-full py-3 text-sm font-semibold">{busy ? "Opening…" : "Confirm tip"}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
